// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useNavigate } from "@tanstack/react-router";
import type { PaginationState, SortingState } from "@tanstack/react-table";
import { Construction, Cpu, MoreHorizontal, Snowflake, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type FirmwareAssignment,
  type FirmwareAssignmentsSortBy,
  useFirmwareAssignmentsQuery,
} from "@/api/hooks/use-firmware";
import { DataTable, type DataTableColumnDef } from "@/components/data-table";
import { ChainLink } from "@/components/shared/chain-link";
import { DateValue } from "@/components/shared/datetime-value";
import { FilterSelect } from "@/components/shared/filter-select";
import { RowId } from "@/components/shared/row-id";
import { StoreLink } from "@/components/shared/store-link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";
import { decodeFilters, encodeFilters } from "@/lib/filter-params";
import { getDefaultPageSize } from "@/lib/preferences";

const SCOPES = ["device", "store", "chain"] as const;

const FILTER_DEFAULTS = {
  search: "",
  scope: "",
  page: 0,
  get pageSize() {
    return getDefaultPageSize();
  },
  sortId: "createdAt",
  sortDesc: true,
};

interface FirmwareAssignmentsTabProps {
  firmwareId: number;
  initialFilters?: string;
}

export function FirmwareAssignmentsTab({ firmwareId, initialFilters }: FirmwareAssignmentsTabProps) {
  const { t } = useTranslation("back-office");
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission(PAGE_PERMISSIONS.hardwareFirmwareEdit);
  const initial = useMemo(() => decodeFilters(initialFilters, FILTER_DEFAULTS), [initialFilters]);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initial.page,
    pageSize: initial.pageSize,
  });
  const [sorting, setSorting] = useState<SortingState>([{ id: initial.sortId, desc: initial.sortDesc }]);
  const [search, setSearch] = useState(initial.search);
  const [scope, setScope] = useState(initial.scope);

  const sortBy = (sorting[0]?.id ?? "createdAt") as FirmwareAssignmentsSortBy;
  const orderBy = sorting[0]?.desc ? ("desc" as const) : ("asc" as const);

  const syncUrl = useCallback(() => {
    const encoded = encodeFilters(
      {
        search,
        scope,
        page: pagination.pageIndex,
        pageSize: pagination.pageSize,
        sortId: sorting[0]?.id ?? "createdAt",
        sortDesc: sorting[0]?.desc ?? true,
      },
      FILTER_DEFAULTS,
    );
    navigate({
      to: "/hardware/firmware/$firmwareId",
      params: { firmwareId: String(firmwareId) },
      search: { t: "assignments", ...(encoded ? { f: encoded } : {}) },
      replace: true,
    });
  }, [search, scope, pagination.pageIndex, pagination.pageSize, sorting, navigate, firmwareId]);

  useEffect(
    function synchronizeFiltersToUrl() {
      syncUrl();
    },
    [syncUrl],
  );

  const { data, isLoading, refetch, isFetching } = useFirmwareAssignmentsQuery(firmwareId, {
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: search || undefined,
    sortBy,
    orderBy,
    scope: (scope || undefined) as (typeof SCOPES)[number] | undefined,
  });

  const rows = useMemo<FirmwareAssignment[]>(() => data?.assignments ?? [], [data?.assignments]);

  // Names live on the relevant chains/stores lists (relevant-entities pattern); resolve by id.
  const chainMap = useMemo(() => new Map((data?.chains ?? []).map((c) => [c.chainId, c])), [data?.chains]);
  const storeMap = useMemo(() => new Map((data?.stores ?? []).map((s) => [s.storeId, s])), [data?.stores]);

  const columns = useMemo<DataTableColumnDef<FirmwareAssignment>[]>(
    () => [
      {
        accessorKey: "firmwareAssignmentId",
        // The API sorts this column under the name `otaAssignmentId` (the response field is
        // `firmwareAssignmentId`); set the column id so sortBy sends the value the enum accepts. CIN-663.
        id: "otaAssignmentId",
        header: t("firmware.assignments.columns.id"),
        size: 80,
        enableHiding: false,
        meta: {
          columnLabel: t("firmware.assignments.columns.id"),
          className: "pl-4",
          forceWidth: true,
          sticky: "left",
        },
        cell: ({ row }) => <RowId id={row.original.firmwareAssignmentId} />,
      },
      {
        id: "target",
        header: t("firmware.assignments.columns.target"),
        enableSorting: false,
        meta: { columnLabel: t("firmware.assignments.columns.target") },
        cell: ({ row }) => {
          const a = row.original;
          if (a.scope === "device") {
            return (
              <span className="inline-flex items-center gap-1.5">
                <Cpu className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="font-mono">{a.deviceName ?? `#${a.deviceId}`}</span>
              </span>
            );
          }
          if (a.scope === "store") {
            const store = a.storeId != null ? storeMap.get(a.storeId) : undefined;
            return store ? (
              <StoreLink storeName={store.storeName} legibleName={store.storeDisplay} />
            ) : (
              <span className="text-muted-foreground">—</span>
            );
          }
          if (a.scope === "chain") {
            const chain = a.chainId != null ? chainMap.get(a.chainId) : undefined;
            return chain ? (
              <ChainLink chainName={chain.chainName} legibleName={chain.legibleName} />
            ) : (
              <span className="text-muted-foreground">—</span>
            );
          }
          return <span className="text-muted-foreground">—</span>;
        },
      },
      {
        id: "scope",
        header: t("firmware.assignments.columns.scope"),
        size: 110,
        enableSorting: false,
        meta: { columnLabel: t("firmware.assignments.columns.scope") },
        cell: ({ row }) => (
          <Badge variant="outline" className="font-normal lowercase text-muted-foreground">
            {t(`firmware.assignments.scope.${row.original.scope}`, row.original.scope)}
          </Badge>
        ),
      },
      {
        id: "frozen",
        accessorKey: "isFrozen",
        header: t("firmware.assignments.columns.frozen"),
        size: 110,
        meta: { columnLabel: t("firmware.assignments.columns.frozen") },
        cell: ({ row }) =>
          row.original.isFrozen ? (
            <Badge
              variant="outline"
              className="gap-1 font-normal lowercase border-sky-300 bg-sky-50 text-sky-700 dark:border-sky-900 dark:bg-sky-950 dark:text-sky-300"
            >
              <Snowflake className="h-3 w-3" />
              {t("firmware.assignments.frozen")}
            </Badge>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: "createdAt",
        accessorKey: "createdAt",
        header: t("firmware.assignments.columns.assignedAt"),
        size: 140,
        meta: { columnLabel: t("firmware.assignments.columns.assignedAt") },
        cell: ({ row }) => <DateValue value={row.original.createdAt} />,
      },
      ...(canEdit
        ? [
            {
              id: "actions",
              size: 50,
              meta: { forceWidth: true, sticky: "right" },
              enableSorting: false,
              enableHiding: false,
              cell: () => (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" sideOffset={-32} className="dropdown-overlap">
                    <DropdownMenuItem disabled>
                      <Construction className="h-4 w-4 text-amber-500" />
                      {t("common.workInProgress")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ),
            } satisfies DataTableColumnDef<FirmwareAssignment>,
          ]
        : []),
    ],
    [t, canEdit, storeMap, chainMap],
  );

  return (
    <div className="p-6">
      <DataTable
        storageKey="firmware-assignments"
        columns={columns}
        data={rows}
        totalCount={data?.count ?? 0}
        isLoading={isLoading}
        pagination={pagination}
        onPaginationChange={setPagination}
        sorting={sorting}
        onSortingChange={setSorting}
        search={search}
        onSearchChange={setSearch}
        searchDebounceMs={1000}
        onRefresh={refetch}
        isRefreshing={isFetching}
        searchPlaceholder={t("firmware.assignments.searchPlaceholder")}
        filters={
          <>
            <FilterSelect
              label={t("firmware.assignments.columns.scope")}
              value={scope}
              onChange={(v) => {
                setScope(v);
                setPagination((prev) => ({ ...prev, pageIndex: 0 }));
              }}
              options={SCOPES.map((s) => ({ label: t(`firmware.assignments.scope.${s}`), value: s }))}
              searchPlaceholder={t("firmware.assignments.searchScopes")}
              emptyText={t("firmware.assignments.noScopes")}
            />
            {scope && (
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 text-muted-foreground hover:text-foreground"
                onClick={() => {
                  setScope("");
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </>
        }
      />
    </div>
  );
}
